import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  createTenantSchema,
  DIVISIONS,
  formatBdMobile,
  type Locale,
  PACKS,
  type PackDef,
  UPCOMING_PACKS,
} from "@zeeta/shared";
import {
  ArrowLeft,
  Check,
  Library,
  Pill,
  Shirt,
  ShoppingBasket,
  Smartphone,
  Store,
  Warehouse,
} from "lucide-react";
import { type FormEvent, useState } from "react";
import { ErrorMessage } from "@/components/error-message";
import { LanguageToggle } from "@/components/language-toggle";
import { Logo } from "@/components/logo";
import { SiteFooter } from "@/components/site-footer";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Field, Input, Select } from "@/components/ui/field";
import { meQuery, refreshMe } from "@/features/auth/queries";
import { useI18n } from "@/i18n";
import { ApiError, api } from "@/lib/api";
import { cn } from "@/lib/cn";
import { allowanceQuery, BusinessLimitNotice } from "./business-allowance";

const PACK_ICONS: Record<string, typeof Store> = {
  "shopping-basket": ShoppingBasket,
  pill: Pill,
  warehouse: Warehouse,
  shirt: Shirt,
  smartphone: Smartphone,
  library: Library,
};

type Step = "profile" | "type" | "details";

interface DetailsForm {
  name: string;
  phone: string;
  divisionId: string;
  districtId: string;
  upazila: string;
  line: string;
  bin: string;
}

export function OnboardingPage() {
  const { t, locale, setLocale } = useI18n();
  const { data: me } = useSuspenseQuery(meQuery);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const needsProfile = !me?.user.name;
  const steps: Step[] = needsProfile ? ["profile", "type", "details"] : ["type", "details"];
  const [step, setStep] = useState<Step>(steps[0] ?? "type");
  const stepIndex = steps.indexOf(step);

  const [name, setName] = useState(me?.user.name ?? "");
  const [pack, setPack] = useState<PackDef | null>(null);
  // Business types as the platform admin set them up; the built-in list until they arrive.
  const offered = useQuery({
    queryKey: ["packs"],
    queryFn: () => api.get<{ packs: PackDef[] }>("/tenants/packs"),
    staleTime: 5 * 60_000,
  });
  const packs = offered.data?.packs ?? PACKS;
  const allowance = useQuery(allowanceQuery);
  const [form, setForm] = useState<DetailsForm>({
    name: "",
    phone: me?.user.phone ? formatBdMobile(me.user.phone).replace("-", "") : "",
    divisionId: "",
    districtId: "",
    upazila: "",
    line: "",
    bin: "",
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const saveProfile = useMutation({
    mutationFn: (input: { name: string; locale: Locale }) => api.patch("/auth/me", input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      setStep("type");
    },
  });

  const createTenant = useMutation({
    mutationFn: (body: unknown) => api.post<{ tenant: { id: string } }>("/tenants", body),
    onSuccess: async ({ tenant }) => {
      await refreshMe(queryClient);
      await navigate({ to: "/t/$tenantId", params: { tenantId: tenant.id } });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.problem?.errors) {
        setFieldErrors(Object.fromEntries(error.problem.errors.map((e) => [e.path, e.message])));
      }
    },
  });

  function submitProfile(event: FormEvent) {
    event.preventDefault();
    if (name.trim().length < 2) {
      setFieldErrors({ name: "Enter your name (at least 2 letters)" });
      return;
    }
    setFieldErrors({});
    saveProfile.mutate({ name: name.trim(), locale });
  }

  function submitDetails(event: FormEvent) {
    event.preventDefault();
    if (!pack) return;
    const body = {
      name: form.name,
      packId: pack.id,
      phone: form.phone,
      address: {
        divisionId: form.divisionId,
        districtId: form.districtId,
        upazila: form.upazila,
        line: form.line,
      },
      ...(form.bin.trim() ? { bin: form.bin.trim() } : {}),
      locale,
    };
    const parsed = createTenantSchema.safeParse(body);
    if (!parsed.success) {
      setFieldErrors(
        Object.fromEntries(parsed.error.issues.map((i) => [i.path.join("."), i.message])),
      );
      return;
    }
    setFieldErrors({});
    createTenant.mutate(body);
  }

  // Server and schema messages are English; Bangla users get a localized prompt per field.
  const err = (path: string) => {
    const message = fieldErrors[path];
    if (!message) return undefined;
    return locale === "en" ? message : t("onboarding.fieldInvalid");
  };

  const division = DIVISIONS.find((d) => d.id === form.divisionId);
  const set = (key: keyof DetailsForm) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <div className="flex min-h-dvh flex-col bg-muted/40">
      <header className="flex items-center justify-between px-4 py-3 sm:px-6">
        <Logo />
        <div className="flex items-center gap-1">
          <ThemeToggle />
          <LanguageToggle />
        </div>
      </header>
      <main className="mx-auto w-full max-w-xl flex-1 px-4 pt-4 pb-16">
        {allowance.data && !allowance.data.canCreate ? (
          <Card>
            <CardContent className="space-y-4">
              <h1 className="text-xl font-semibold tracking-tight">{t("businessLimit.title")}</h1>
              <BusinessLimitNotice allowance={allowance.data} />
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">
                {t("onboarding.stepOf", { step: stepIndex + 1, total: steps.length })}
              </p>
              <ol className="flex gap-1.5" aria-hidden>
                {steps.map((s, i) => (
                  <li
                    key={s}
                    className={cn(
                      "h-1.5 w-8 rounded-full",
                      i <= stepIndex ? "bg-primary" : "bg-border",
                    )}
                  />
                ))}
              </ol>
            </div>

            {step === "profile" && (
              <Card>
                <CardContent>
                  <form onSubmit={submitProfile} className="space-y-6" noValidate>
                    <div className="space-y-1.5">
                      <h1 className="text-xl font-semibold tracking-tight">
                        {t("onboarding.profileTitle")}
                      </h1>
                      <p className="text-sm text-muted-foreground">
                        {t("onboarding.profileSubtitle")}
                      </p>
                    </div>
                    <Field label={t("onboarding.yourName")} error={err("name")}>
                      {(p) => (
                        <Input
                          {...p}
                          autoFocus
                          autoComplete="name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      )}
                    </Field>
                    <fieldset className="space-y-2">
                      <legend className="text-sm font-medium">
                        {t("onboarding.languageTitle")}
                      </legend>
                      <div className="grid grid-cols-2 gap-3">
                        {(["bn", "en"] as const).map((l) => (
                          <button
                            type="button"
                            key={l}
                            onClick={() => setLocale(l)}
                            aria-pressed={locale === l}
                            className={cn(
                              "rounded-lg border p-3 text-left text-sm font-medium transition-colors",
                              locale === l
                                ? "border-primary bg-accent text-accent-foreground"
                                : "hover:bg-muted",
                            )}
                          >
                            {l === "bn" ? "বাংলা" : "English"}
                          </button>
                        ))}
                      </div>
                    </fieldset>
                    <ErrorMessage error={saveProfile.error} />
                    <Button
                      type="submit"
                      size="lg"
                      className="w-full"
                      loading={saveProfile.isPending}
                    >
                      {t("common.continue")}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {step === "type" && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <h1 className="text-xl font-semibold tracking-tight">
                    {t("onboarding.typeTitle")}
                  </h1>
                  <p className="text-sm text-muted-foreground">{t("onboarding.typeSubtitle")}</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {packs.map((p) => {
                    const Icon = PACK_ICONS[p.icon] ?? Store;
                    return (
                      <button
                        type="button"
                        key={p.id}
                        onClick={() => {
                          setPack(p);
                          setStep("details");
                        }}
                        className={cn(
                          "group flex flex-col gap-3 rounded-xl border bg-card p-4 text-left shadow-xs transition-colors hover:border-primary",
                          pack?.id === p.id && "border-primary ring-1 ring-primary",
                        )}
                      >
                        <span className="flex size-10 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                          <Icon className="size-5" aria-hidden />
                        </span>
                        <span>
                          <span className="block font-semibold">{p.name[locale]}</span>
                          <span className="block text-sm text-muted-foreground">
                            {p.description[locale]}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                  {UPCOMING_PACKS.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between rounded-xl border border-dashed p-4 text-sm text-muted-foreground"
                    >
                      <span>{p.name[locale]}</span>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                        {t("onboarding.comingSoon")}
                      </span>
                    </div>
                  ))}
                </div>
                {needsProfile && (
                  <Button variant="ghost" onClick={() => setStep("profile")}>
                    <ArrowLeft aria-hidden />
                    {t("common.back")}
                  </Button>
                )}
              </div>
            )}

            {step === "details" && pack && (
              <Card>
                <CardContent>
                  <form onSubmit={submitDetails} className="space-y-5" noValidate>
                    <div className="space-y-1.5">
                      <p className="inline-flex items-center gap-1.5 rounded-full bg-accent px-2.5 py-0.5 text-xs font-medium text-accent-foreground">
                        <Check className="size-3" aria-hidden />
                        {pack.name[locale]}
                      </p>
                      <h1 className="text-xl font-semibold tracking-tight">
                        {t("onboarding.detailsTitle")}
                      </h1>
                      <p className="text-sm text-muted-foreground">
                        {t("onboarding.detailsSubtitle")}
                      </p>
                    </div>
                    <Field label={t("onboarding.shopName")} error={err("name")}>
                      {(p) => (
                        <Input
                          {...p}
                          autoFocus
                          autoComplete="organization"
                          value={form.name}
                          onChange={(e) => set("name")(e.target.value)}
                        />
                      )}
                    </Field>
                    <Field label={t("onboarding.shopPhone")} error={err("phone")}>
                      {(p) => (
                        <Input
                          {...p}
                          type="tel"
                          inputMode="tel"
                          value={form.phone}
                          onChange={(e) => set("phone")(e.target.value)}
                        />
                      )}
                    </Field>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label={t("onboarding.division")} error={err("address.divisionId")}>
                        {(p) => (
                          <Select
                            {...p}
                            value={form.divisionId}
                            onChange={(e) =>
                              setForm((f) => ({ ...f, divisionId: e.target.value, districtId: "" }))
                            }
                          >
                            <option value="">{t("onboarding.selectPlaceholder")}</option>
                            {DIVISIONS.map((d) => (
                              <option key={d.id} value={d.id}>
                                {d[locale]}
                              </option>
                            ))}
                          </Select>
                        )}
                      </Field>
                      <Field label={t("onboarding.district")} error={err("address.districtId")}>
                        {(p) => (
                          <Select
                            {...p}
                            value={form.districtId}
                            disabled={!division}
                            onChange={(e) => set("districtId")(e.target.value)}
                          >
                            <option value="">{t("onboarding.selectPlaceholder")}</option>
                            {division?.districts.map((d) => (
                              <option key={d.id} value={d.id}>
                                {d[locale]}
                              </option>
                            ))}
                          </Select>
                        )}
                      </Field>
                    </div>
                    <Field label={t("onboarding.upazila")} error={err("address.upazila")}>
                      {(p) => (
                        <Input
                          {...p}
                          value={form.upazila}
                          onChange={(e) => set("upazila")(e.target.value)}
                        />
                      )}
                    </Field>
                    <Field label={t("onboarding.addressLine")} error={err("address.line")}>
                      {(p) => (
                        <Input
                          {...p}
                          autoComplete="street-address"
                          placeholder={t("onboarding.addressPlaceholder")}
                          value={form.line}
                          onChange={(e) => set("line")(e.target.value)}
                        />
                      )}
                    </Field>
                    <Field
                      label={t("onboarding.bin")}
                      optionalLabel={t("common.optional")}
                      hint={t("onboarding.binHelp")}
                      error={err("bin")}
                    >
                      {(p) => (
                        <Input
                          {...p}
                          inputMode="numeric"
                          value={form.bin}
                          onChange={(e) => set("bin")(e.target.value)}
                        />
                      )}
                    </Field>
                    <ErrorMessage error={createTenant.error} />
                    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
                      <Button variant="ghost" onClick={() => setStep("type")}>
                        <ArrowLeft aria-hidden />
                        {t("common.back")}
                      </Button>
                      <Button type="submit" size="lg" loading={createTenant.isPending}>
                        {createTenant.isPending ? t("onboarding.creating") : t("onboarding.create")}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
